import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileSignature, CheckCircle2 } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useSeo } from "../lib/useSeo";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";

export default function SignNda() {
  useSeo({ title: "Sign NDA", noindex: true });
  const [params] = useSearchParams();
  const token = params.get("token");
  const [nda, setNda] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setErr("Missing signing token — please use the link from your email."); return; }
    api.get(`/nda/${token}`).then((r) => {
      setNda(r.data);
      if (r.data.status === "signed") setDone(true);
      setName(r.data.signer_name || "");
      setTitle(r.data.signer_title || "");
    }).catch((e) => setErr(apiError(e)));
  }, [token]);

  const sign = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/nda/${token}/sign`, { signed_name: name, signed_title: title, agree });
      setDone(true);
    } catch (e2) { setErr(apiError(e2)); }
    setBusy(false);
  };

  return (
    <div className="min-h-[80vh] bg-[#FAFAF7] py-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <h1 className="font-display text-2xl font-extrabold tracking-tight">NDA Signing</h1>
        </div>
        {err && !done && (
          <div className="bg-white border border-red-200 rounded-2xl p-8 text-center" data-testid="nda-sign-error">
            <p className="font-bold text-red-600">{err}</p>
            <p className="text-sm text-slate-500 mt-2">Contact info@virtualautochat.uk if you need a new signing link.</p>
          </div>
        )}
        {done && (
          <div className="bg-white border border-emerald-200 rounded-2xl p-10 text-center" data-testid="nda-signed-confirmation">
            <CheckCircle2 size={44} className="text-emerald-600 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold">NDA fully executed</h1>
            <p className="text-slate-500 mt-3 max-w-md mx-auto">Thank you{nda?.company_name ? ` — the agreement between Voliciti Group Ventures Ltd and ${nda.company_name} is now in force` : ""}. A signed PDF copy has been emailed to you for your records.</p>
          </div>
        )}
        {nda && !done && !err && (
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-10" data-testid="nda-sign-page">
            <div className="flex items-center gap-2 text-emerald-700 font-bold text-sm uppercase tracking-wide mb-2"><FileSignature size={16} /> Electronic signature request</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold">Mutual Non-Disclosure Agreement</h1>
            <p className="text-slate-500 mt-2 text-sm">Between <strong>Voliciti Group Ventures Ltd</strong> and <strong>{nda.company_name}</strong> · Governing law: {nda.governing_law}</p>
            <div className="mt-6 max-h-[420px] overflow-y-auto border border-slate-200 rounded-xl p-5 bg-slate-50/60" data-testid="nda-text">
              {nda.clauses.map((c) => (
                <div key={c.title} className="mb-4">
                  <p className="font-bold text-sm text-emerald-800">{c.title}</p>
                  <p className="text-sm text-slate-600 leading-relaxed mt-1">{c.text}</p>
                </div>
              ))}
              <p className="text-xs text-slate-400">Voliciti Group Ventures Ltd's signature is applied electronically upon issue of this agreement.</p>
            </div>
            <form onSubmit={sign} className="mt-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-3">
                <div><label className="text-xs font-bold text-slate-500">Type your full legal name (this is your signature) *</label>
                  <input className={inputCls} required value={name} onChange={(e) => setName(e.target.value)} data-testid="nda-sign-name" /></div>
                <div><label className="text-xs font-bold text-slate-500">Job title</label>
                  <input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} data-testid="nda-sign-title" /></div>
              </div>
              <label className="flex items-start gap-2.5 text-sm text-slate-700">
                <input type="checkbox" className="mt-0.5" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="nda-sign-agree" />
                <span>I confirm I am authorised to sign on behalf of <strong>{nda.company_name}</strong>, I have read and agree to this Mutual Non-Disclosure Agreement, and I consent to signing it electronically. My typed name, the date/time and my IP address will be recorded as my signature.</span>
              </label>
              <button type="submit" disabled={!agree || !name.trim() || busy}
                className="w-full sm:w-auto bg-emerald-700 hover:bg-emerald-800 text-white font-bold px-8 py-3.5 rounded-xl transition-colors disabled:opacity-50" data-testid="nda-sign-submit">
                {busy ? "Signing…" : "Sign agreement"}
              </button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
